import { useEffect, useRef } from 'react';

import ROUTES from '../config/routes';
import mqcapitalLogoBlack from '../assets/icons/mqcapital-logo-black.png';
import mqcapitalLogoWhite from '../assets/icons/mqcapital-logo-white.png';

const fileSignalTermsAndConditions =
  'https://www.dropbox.com/scl/fi/db8eghrna1dzzkusilivh/file-signal-terms-and-conditions.pdf?rlkey=ddex4bklfpcxz884x9xpo112k&st=pegw51yx&raw=1';
const fileFaqMqcapital =
  'https://www.dropbox.com/scl/fi/7jze9fhp0t3jn8tajofq3/file-faq-mqcapital.pdf?rlkey=41b1ujohb4teglbp15yzbtf0q&st=ntdp5kib&raw=1';
const fileSecuritizationLuxembourg =
  'https://www.dropbox.com/scl/fi/swjhv1y4zo56k6fb291ha/file-securitization.pdf?rlkey=yyo7cnmuxd6mdeobegvqrdqst&st=g6ye8vrd&raw=1';

export const universalScrollTo = (top, left = 0, behavior = 'auto') => {
  try {
    window.scrollTo({ top, left, behavior });
  } catch (e) {
    // Fallback silently
  }

  try {
    document.body.scrollTo({ top, left, behavior });
  } catch (e) {
    // Fallback silently
  }

  setTimeout(() => {
    try {
      window.scrollTo({ top, left, behavior: 'auto' });
      document.documentElement.scrollTop = top;
    } catch (e) {
      // Silent fallback
    }
  }, 10);
};

export const useIntersectionObserver = (sectionIds, setActiveSection) => {
  const observerRef = useRef(null);
  const sectionsVisibilityRef = useRef({});

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const handleIntersection = entries => {
      entries.forEach(entry => {
        const sectionId = entry.target.id;
        sectionsVisibilityRef.current[sectionId] = {
          isIntersecting: entry.isIntersecting,
          ratio: entry.intersectionRatio,
          top: entry.boundingClientRect.top
        };
      });

      let mostVisibleSection = null;
      let highestRatio = 0;
      let closestToTop = Infinity;

      Object.keys(sectionsVisibilityRef.current).forEach(id => {
        const section = sectionsVisibilityRef.current[id];

        if (section.isIntersecting) {
          if (section.ratio > highestRatio) {
            mostVisibleSection = id;
            highestRatio = section.ratio;
            closestToTop = section.top;
          } else if (
            section.ratio === highestRatio &&
            section.top < closestToTop
          ) {
            mostVisibleSection = id;
            closestToTop = section.top;
          }
        }
      });

      if (!mostVisibleSection) {
        let closestAbove = null;
        let closestDistance = Infinity;

        Object.keys(sectionsVisibilityRef.current).forEach(id => {
          const section = sectionsVisibilityRef.current[id];
          if (section.top < 0 && Math.abs(section.top) < closestDistance) {
            closestAbove = id;
            closestDistance = Math.abs(section.top);
          }
        });

        mostVisibleSection = closestAbove || sectionIds[0];
      }

      if (mostVisibleSection) {
        setActiveSection(mostVisibleSection);
      }
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '-80px 0px -50% 0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
    });

    sectionIds.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sectionIds, setActiveSection]);
};

export const isMainRouteCheck = path => {
  const mainRoutes = [
    ROUTES.HOME,
    ROUTES.AUTH,
    ROUTES.ABOUT_US.ROOT,
    ROUTES.STRATEGIES.ROOT,
    ROUTES.HOW_TO_JOIN.ROOT,
    ROUTES.LIBRARY,
    ROUTES.CONTACT,
    ROUTES.LEGAL.ROOT
  ];
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  return mainRoutes.includes(normalizedPath);
};

/**
 * DEPRECATED: Use navigateToSection from NavigationContext instead
 * This function is kept for backward compatibility only
 * @param {string} id - Section ID
 * @param {string} path - Target path
 * @param {Object} refs - Legacy refs object
 * @param {Function} navigate - React Router navigate function
 * @param {string} currentPath - Current pathname
 * @param {Function} startPageTransition - Page transition function
 */
export const navigateToPathOrSection = (
  id,
  path,
  refs,
  navigate,
  currentPath,
  startPageTransition
) => {
  // eslint-disable-next-line no-console
  console.warn(
    'navigateToPathOrSection is deprecated. Use navigateToSection from NavigationContext instead'
  );

  const targetPath = id ? `${path}#${id}` : path;
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  const normalizedCurrentPath =
    currentPath !== '/' && currentPath.endsWith('/')
      ? currentPath.slice(0, -1)
      : currentPath;

  if (normalizedPath !== normalizedCurrentPath) {
    if (!id && startPageTransition && isMainRouteCheck(normalizedPath)) {
      startPageTransition(() => {
        navigate(targetPath);
      });
    } else {
      navigate(targetPath);
    }
  } else {
    if (id) {
      const element =
        document.getElementById(id) || (refs && refs[id]?.current);
      if (element) {
        const offset = 100;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;

        universalScrollTo(offsetPosition, 0, 'smooth');

        if (window.location.hash !== `#${id}`) {
          navigate(targetPath, { replace: true });
        }
      } else {
        navigate(targetPath, { replace: true });
      }
    } else {
      universalScrollTo(0, 0, 'auto');
      if (window.location.hash || path !== currentPath) {
        navigate(path);
      }
    }
  }
};

/**
 * PDF Files Registry
 * Maps PDF IDs to imported PDF files
 * Used for opening PDFs in new tabs
 */
export const pdfFiles = {
  'signal-terms-and-conditions': fileSignalTermsAndConditions,
  'faq-mqcapital': fileFaqMqcapital,
  'securitization-luxembourg': fileSecuritizationLuxembourg
};

/**
 * Get logo based on text color
 * @param {string} textColor - Text color (hex)
 * @returns {string} Logo path
 */
export const getLogo = textColor => {
  return textColor === '#ffffff' ? mqcapitalLogoWhite : mqcapitalLogoBlack;
};

/**
 * DEPRECATED: Use navigationMenu from config/navigation.js instead
 * This function is kept for backward compatibility only
 * @returns {Array} Navigation menu items
 */
export const getNavigationMenuItems = () => {
  // eslint-disable-next-line no-console
  console.warn(
    'getNavigationMenuItems is deprecated. Use navigationMenu from config/navigation.js instead'
  );
  // Import the new navigation config
  const { navigationMenu } = require('../config/navigation');
  return navigationMenu;
};

export const isActivePath = (path, currentLocation) => {
  if (!path || !currentLocation) return false;
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  const normalizedCurrentLocation =
    currentLocation !== '/' && currentLocation.endsWith('/')
      ? currentLocation.slice(0, -1)
      : currentLocation;
  if (normalizedPath === normalizedCurrentLocation) return true;
  return (
    normalizedPath !== '/' &&
    normalizedCurrentLocation.startsWith(normalizedPath + '/')
  );
};

export const getParentSectionPath = path => {
  if (!path || path === '/') return null;
  const pathParts = path.split('/').filter(Boolean);
  if (pathParts.length <= 1) return `/${pathParts[0]}`;
  return `/${pathParts[0]}`;
};

export const getSectionName = path => {
  const parentPath = getParentSectionPath(path);
  switch (parentPath) {
    case '/about-us':
      return 'About Us';
    case '/strategies':
      return 'Strategies';
    case '/how-to-join':
      return 'How To Join';
    case '/library':
      return 'Library';
    case '/legal':
      return 'Legal';
    case '/contact':
      return 'Contact';
    case '/login':
    case '/auth':
      return 'Login';
    case '/jobs':
      return 'Jobs';
    default:
      return 'Home';
  }
};

export const generateBreadcrumbs = path => {
  const breadcrumbs = [{ label: 'Home', path: ROUTES.HOME }];
  if (!path || path === '/') return breadcrumbs;
  const pathSegments = path.split('/').filter(Boolean);
  let currentPath = '';
  if (pathSegments[0] === 'login' || pathSegments[0] === 'auth') {
    return [
      { label: 'Home', path: ROUTES.HOME },
      { label: 'Portal', path: '/auth' }
    ];
  }
  if (
    pathSegments.length >= 3 &&
    pathSegments[0] === 'how-to-join' &&
    pathSegments[1] === 'interview' &&
    pathSegments[2] === 'preparing-for-interview'
  ) {
    return [
      { label: 'Home', path: ROUTES.HOME },
      { label: 'How To Join', path: ROUTES.HOW_TO_JOIN.ROOT },
      { label: 'Interview', path: ROUTES.HOW_TO_JOIN.INTERVIEW },
      {
        label: 'Preparing For Interview',
        path: ROUTES.HOW_TO_JOIN.PREPARING_INTERVIEW
      }
    ];
  }
  if (
    pathSegments.length === 2 &&
    pathSegments[0] === 'how-to-join' &&
    ![
      'internship',
      'interview',
      'uk-recruitment-policy',
      'eu-recruitment-policy'
    ].includes(pathSegments[1])
  ) {
    breadcrumbs.push({ label: 'How To Join', path: ROUTES.HOW_TO_JOIN.ROOT });
    const jobId = pathSegments[1];
    try {
      const { isInternship } = require('../data/static/jobData');
      if (isInternship(jobId)) {
        breadcrumbs.push({
          label: 'Internships',
          path: ROUTES.HOW_TO_JOIN.INTERNSHIP
        });
      }
    } catch (error) {
      // Job data module not available, continue without internship breadcrumb
    }
    const jobTitle = jobId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    breadcrumbs.push({ label: jobTitle, path: `/how-to-join/${jobId}` });
    return breadcrumbs;
  }
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    if (index === 0) {
      breadcrumbs.push({
        label: getSectionName(currentPath),
        path: currentPath
      });
      return;
    }
    const label = segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    breadcrumbs.push({ label, path: currentPath });
  });
  return breadcrumbs;
};
